import { useState, useEffect } from "react";
import axios from "axios";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import {
  School, Save, Loader2, Plus, X, Trash2, GripVertical,
} from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export function ForSchoolsCMS({ darkMode, token, viewOnly }) {
  const [config, setConfig] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [newSubject, setNewSubject] = useState("");
  const [newLevel, setNewLevel] = useState("");
  const [newRequest, setNewRequest] = useState({ key: "", label: "" });

  const dm = darkMode;
  const cardBg = dm ? "bg-gray-800/80 border border-gray-700/50" : "";
  const valText = dm ? "text-white" : "";
  const labelText = dm ? "text-gray-300" : "";
  const inputClass = dm ? "bg-gray-700/50 border-gray-600 text-white" : "";
  const headers = { Authorization: `Bearer ${token}` };

  useEffect(() => {
    if (!token) return;
    axios.get(`${API}/admin/school-config`, { headers })
      .then(res => setConfig(res.data))
      .catch(() => toast.error("Failed to load school config"))
      .finally(() => setLoading(false));
  }, [token]);

  const handleSave = async () => {
    setSaving(true);
    try {
      await axios.put(`${API}/admin/school-config`, config, { headers });
      toast.success("School config saved!");
    } catch {
      toast.error("Failed to save");
    } finally { setSaving(false); }
  };

  const updateField = (field, value) => setConfig(prev => ({ ...prev, [field]: value }));

  const updateFeature = (index, field, value) => {
    const features = [...(config.features || [])];
    features[index] = { ...features[index], [field]: value };
    updateField("features", features);
  };

  const updateTier = (index, field, value) => {
    const tiers = [...(config.pricing_tiers || [])];
    tiers[index] = { ...tiers[index], [field]: value };
    updateField("pricing_tiers", tiers);
  };

  const addSubject = () => {
    if (!newSubject.trim() || (config.subjects || []).includes(newSubject.trim())) return;
    updateField("subjects", [...(config.subjects || []), newSubject.trim()]);
    setNewSubject("");
  };

  const removeSubject = (s) => updateField("subjects", (config.subjects || []).filter(x => x !== s));

  const addLevel = () => {
    if (!newLevel.trim() || (config.class_levels || []).includes(newLevel.trim())) return;
    updateField("class_levels", [...(config.class_levels || []), newLevel.trim()]);
    setNewLevel("");
  };

  const removeLevel = (l) => updateField("class_levels", (config.class_levels || []).filter(x => x !== l));

  const addSpecialRequest = () => {
    if (!newRequest.label.trim()) return;
    const key = newRequest.key.trim() || newRequest.label.trim().toLowerCase().replace(/[^a-z0-9]/g, "_");
    updateField("special_requests", [...(config.special_requests || []), { key, label: newRequest.label.trim() }]);
    setNewRequest({ key: "", label: "" });
  };

  const removeSpecialRequest = (key) => updateField("special_requests", (config.special_requests || []).filter(x => x.key !== key));

  if (loading) return <div className="text-center py-12"><Loader2 className="w-6 h-6 animate-spin mx-auto text-gray-400" /></div>;
  if (!config) return <p className="text-center py-8 text-muted-foreground">Failed to load configuration</p>;

  return (
    <div className="space-y-6" data-testid="for-schools-cms">
      {/* Save Button */}
      <div className="flex justify-end">
        <Button onClick={handleSave} disabled={saving || viewOnly} className="gap-2" data-testid="save-school-config">
          {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Save className="w-4 h-4" />}
          Save Changes
        </Button>
      </div>

      {/* Hero Section */}
      <Card className={`${cardBg} rounded-xl`}>
        <CardHeader className="pb-3">
          <CardTitle className={`text-base ${valText}`}>Hero Section</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="space-y-2">
            <Label className={labelText}>Hero Title</Label>
            <Input value={config.hero_title || ""} onChange={(e) => updateField("hero_title", e.target.value)} className={inputClass} disabled={viewOnly} data-testid="school-hero-title-input" />
          </div>
          <div className="space-y-2">
            <Label className={labelText}>Hero Description</Label>
            <Textarea value={config.hero_description || ""} onChange={(e) => updateField("hero_description", e.target.value)} className={inputClass} rows={3} disabled={viewOnly} data-testid="school-hero-desc-input" />
          </div>
        </CardContent>
      </Card>

      {/* Feature Cards */}
      <Card className={`${cardBg} rounded-xl`}>
        <CardHeader className="pb-3">
          <CardTitle className={`text-base ${valText}`}>Feature Cards (4)</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          {(config.features || []).map((feature, i) => (
            <div key={i} className={`grid sm:grid-cols-2 gap-3 p-3 rounded-lg ${dm ? "bg-gray-700/30" : "bg-gray-50"}`}>
              <div className="space-y-1">
                <Label className={`text-xs ${labelText}`}>Title</Label>
                <Input value={feature.title} onChange={(e) => updateFeature(i, "title", e.target.value)} className={`h-8 text-sm ${inputClass}`} disabled={viewOnly} />
              </div>
              <div className="space-y-1">
                <Label className={`text-xs ${labelText}`}>Description</Label>
                <Input value={feature.description} onChange={(e) => updateFeature(i, "description", e.target.value)} className={`h-8 text-sm ${inputClass}`} disabled={viewOnly} />
              </div>
            </div>
          ))}
        </CardContent>
      </Card>

      {/* Pricing Tiers */}
      <Card className={`${cardBg} rounded-xl`}>
        <CardHeader className="pb-3">
          <CardTitle className={`text-base ${valText}`}>Pricing Tiers</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid sm:grid-cols-3 gap-4">
            {(config.pricing_tiers || []).map((tier, i) => (
              <div key={i} className={`p-3 rounded-lg space-y-2 ${dm ? "bg-gray-700/30" : "bg-gray-50"}`}>
                <div className="space-y-1">
                  <Label className={`text-xs ${labelText}`}>Price (GH&#8373;)</Label>
                  <Input value={tier.price} onChange={(e) => updateTier(i, "price", e.target.value)} className={`h-8 text-sm ${inputClass}`} disabled={viewOnly} />
                </div>
                <div className="space-y-1">
                  <Label className={`text-xs ${labelText}`}>Range</Label>
                  <Input value={tier.range} onChange={(e) => updateTier(i, "range", e.target.value)} className={`h-8 text-sm ${inputClass}`} disabled={viewOnly} />
                </div>
                <div className="space-y-1">
                  <Label className={`text-xs ${labelText}`}>Discount Text</Label>
                  <Input value={tier.discount} onChange={(e) => updateTier(i, "discount", e.target.value)} className={`h-8 text-sm ${inputClass}`} disabled={viewOnly} />
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>

      {/* Subjects */}
      <Card className={`${cardBg} rounded-xl`}>
        <CardHeader className="pb-3">
          <CardTitle className={`text-base ${valText}`}>Form: Subjects</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {(config.subjects || []).map(s => (
              <Badge key={s} variant="secondary" className="gap-1 pr-1">
                {s}
                {!viewOnly && <button onClick={() => removeSubject(s)} className="ml-1 hover:text-red-500"><X className="w-3 h-3" /></button>}
              </Badge>
            ))}
          </div>
          {!viewOnly && (
            <div className="flex gap-2">
              <Input value={newSubject} onChange={(e) => setNewSubject(e.target.value)} placeholder="Add subject..." className={`h-8 text-sm ${inputClass}`} onKeyDown={(e) => e.key === "Enter" && addSubject()} data-testid="add-subject-input" />
              <Button size="sm" variant="outline" onClick={addSubject} className={`h-8 gap-1 ${dm ? "border-gray-500 text-white hover:bg-gray-700" : ""}`} data-testid="add-subject-btn"><Plus className="w-3 h-3" />Add</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Class Levels */}
      <Card className={`${cardBg} rounded-xl`}>
        <CardHeader className="pb-3">
          <CardTitle className={`text-base ${valText}`}>Form: Class Levels</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          <div className="flex flex-wrap gap-2">
            {(config.class_levels || []).map(l => (
              <Badge key={l} variant="secondary" className="gap-1 pr-1">
                {l}
                {!viewOnly && <button onClick={() => removeLevel(l)} className="ml-1 hover:text-red-500"><X className="w-3 h-3" /></button>}
              </Badge>
            ))}
          </div>
          {!viewOnly && (
            <div className="flex gap-2">
              <Input value={newLevel} onChange={(e) => setNewLevel(e.target.value)} placeholder="Add class level..." className={`h-8 text-sm ${inputClass}`} onKeyDown={(e) => e.key === "Enter" && addLevel()} data-testid="add-level-input" />
              <Button size="sm" variant="outline" onClick={addLevel} className={`h-8 gap-1 ${dm ? "border-gray-500 text-white hover:bg-gray-700" : ""}`} data-testid="add-level-btn"><Plus className="w-3 h-3" />Add</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Special Requests */}
      <Card className={`${cardBg} rounded-xl`}>
        <CardHeader className="pb-3">
          <CardTitle className={`text-base ${valText}`}>Form: Special Request Options</CardTitle>
        </CardHeader>
        <CardContent className="space-y-3">
          {(config.special_requests || []).map(sr => (
            <div key={sr.key} className={`flex items-center justify-between gap-2 p-2 rounded-lg ${dm ? "bg-gray-700/30" : "bg-gray-50"}`}>
              <span className={`text-sm ${valText}`}>{sr.label}</span>
              {!viewOnly && (
                <button onClick={() => removeSpecialRequest(sr.key)} className="text-gray-400 hover:text-red-500"><Trash2 className="w-3.5 h-3.5" /></button>
              )}
            </div>
          ))}
          {!viewOnly && (
            <div className="flex gap-2">
              <Input value={newRequest.label} onChange={(e) => setNewRequest(r => ({ ...r, label: e.target.value }))} placeholder="e.g. Include answer key booklet" className={`h-8 text-sm flex-1 ${inputClass}`} onKeyDown={(e) => e.key === "Enter" && addSpecialRequest()} data-testid="add-request-input" />
              <Button size="sm" variant="outline" onClick={addSpecialRequest} className={`h-8 gap-1 ${dm ? "border-gray-500 text-white hover:bg-gray-700" : ""}`} data-testid="add-request-btn"><Plus className="w-3 h-3" />Add</Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Quantity Tooltip */}
      <Card className={`${cardBg} rounded-xl`}>
        <CardHeader className="pb-3">
          <CardTitle className={`text-base ${valText}`}>Form: Quantity Tooltip</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            <Label className={labelText}>Tooltip text shown when users hover over "Total Quantity (per exam)"</Label>
            <Textarea value={config.quantity_tooltip || ""} onChange={(e) => updateField("quantity_tooltip", e.target.value)} className={inputClass} rows={2} disabled={viewOnly} data-testid="quantity-tooltip-input" />
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
